import { RIYAL } from "@/lib/currency";
import { useEffect, useMemo, useState } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { usePortalBase } from "@/hooks/usePortalBase";
import { useAuth } from "@/hooks/useAuth";
import { getUnitDisplayLabel, getPurposeLabel } from "@/lib/propertyUnitMatrix";
import { useProperties } from "@/hooks/useProperties";
import { useUnits } from "@/hooks/useUnits";
import { useTenants, Tenant } from "@/hooks/useTenants";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/ui/date-picker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { AlertCircle, Loader2, Plus, Trash2 } from "lucide-react";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { AddTenantDialog } from "@/components/tenants/AddTenantDialog";
import {
  deriveTenantPaidServicesTotal,
  addDurationInclusive,
  diffDurationInclusive,
  contractMonthsFromDates,
  isDurationEntered,
  computeDownPayment,
  saleScheduledBalance,
} from "@/lib/contractHelpers";
import { formatNumber } from "@/lib/formatters";
import { DurationFields } from "./DurationFields";
import {
  ContractWizardFormData,
  ContractUnitFormRow,
  emptyContractUnitRow,
  emptyServiceEntry,
  CONTRACT_TYPES,
  SALE_TYPES,
  payerOptions,
  FEE_TYPES,
  FeeType,
  FeeConfig,
  Payer,
  PaymentMethod,
  PAYMENT_METHODS,
  SaleType,
  UtilityType,
  SERVICE_FEE_KEYS,
  SERVICE_UTILITY_KEYS,
  SERVICE_LABELS,
  UTILITY_TYPES_WITH_NA,
} from "./types";
import { FeeRow } from "./FeeRow";

interface ContractInfoStepProps {
  formData: ContractWizardFormData;
  updateFormData: (
    updates: Partial<ContractWizardFormData> | ((prev: ContractWizardFormData) => ContractWizardFormData)
  ) => void;
  errors: Record<string, string>;
  setErrors: (errors: Record<string, string>) => void;
}

/**
 * Whether to show the "no properties assigned" alert in the contract wizard.
 * Employees are scoped to their assigned properties (see useProperties); with
 * zero assignments the property dropdown is empty and the wizard is a silent
 * dead-end. Only employees are scoped this way, and we wait for loading to
 * finish before deciding so the alert doesn't flash during fetch.
 */
export function shouldShowNoPropertiesAlert(
  activeRole: string | null | undefined,
  propertiesLoading: boolean,
  propertyCount: number
): boolean {
  return activeRole === "employee" && !propertiesLoading && propertyCount === 0;
}

export function ContractInfoStep({
  formData,
  updateFormData,
  errors,
  setErrors,
}: ContractInfoStepProps) {
  const { t, language } = useLanguage();
  const { activeRole } = useAuth();
  // Brokerage is a dashboard-only flow (its routes live under /dashboard); hide
  // the brokerage contract type in role portals so it never dead-ends there.
  const portalBase = usePortalBase();
  const contractTypeOptions = CONTRACT_TYPES.filter(
    (ty) => ty.value !== "brokerage" || portalBase === "/dashboard",
  );
  // Sale-structure flags drive the whole step: immediate sales hide the term
  // (end/duration) and annual services and carry a preferred payment channel
  // inline; off-plan sales relabel the end date as a delivery date.
  const isSale = formData.contractType === "sale";
  const isImmediateSale = isSale && formData.saleType === "immediate";
  const isOffplanSale = isSale && formData.saleType === "offplan";
  const [showAddTenantDialog, setShowAddTenantDialog] = useState(false);

  const { data: properties = [], isLoading: propertiesLoading } = useProperties();
  const employeeHasNoProperties = shouldShowNoPropertiesAlert(
    activeRole,
    propertiesLoading,
    properties.length
  );
  const { data: units = [], isLoading: unitsLoading } = useUnits(formData.propertyId || undefined);
  const { data: tenants = [], isLoading: tenantsLoading } = useTenants();

  // Edit-mode visibility: include units that are already saved on THIS
  // contract even though create_contract_with_invoices marked them
  // status='rented'. Without this, the picker would be empty on edit (the
  // saved units never matched `status === "available"`), making it look
  // to the user like all unit data was lost — see QA report 2026-05-17.
  // Units rented to OTHER contracts stay hidden — only this contract's
  // own unit IDs are re-included.
  const savedUnitIds = useMemo(
    () => new Set(formData.units.map((r) => r.unitId).filter(Boolean)),
    [formData.units]
  );
  const availableUnits = units.filter(
    (u) => u.status === "available" || savedUnitIds.has(u.id)
  );

  // Auto-sync contract-level totalAmount to the sum of per-unit annual
  // rents for rent/sublease contracts. The Rent Amount field is always
  // read-only for these contract types — the user sets rent per-unit and
  // this effect keeps the aggregate in sync so validation, payment
  // schedule generation, and fee math all see the correct total.
  // Sale contracts keep their totalAmount editable as before.
  useEffect(() => {
    const filledUnitCount = formData.units.filter((u) => u.unitId).length;
    const isRentOrSublease =
      formData.contractType === "rent" || formData.contractType === "sublease";
    if (!isRentOrSublease || filledUnitCount < 1) return;
    const sum = formData.units.reduce(
      (acc, u) => acc + (u.rentAmount ? Number(u.rentAmount) : 0),
      0
    );
    if (sum > 0 && String(sum) !== formData.totalAmount) {
      updateFormData({ totalAmount: String(sum) });
    }
  }, [formData.units, formData.contractType]);

  // ── Ejar-style duration ↔ dates two-way binding ──────────────────────────
  // Fully handler-driven (no effects reacting to each other) so there is no
  // update-loop risk: each user action computes the OTHER derived values in
  // the SAME updateFormData call. `durationValue`/`durationUnit` (legacy,
  // no longer shown in the UI) are kept in sync with the real date span so
  // the payment-schedule generator downstream (ContractWizardDrawer's
  // `contractMonths`) stays correct regardless of which path the user took.
  const durationYmd = {
    years: Number(formData.durationYears) || 0,
    months: Number(formData.durationMonths) || 0,
    days: Number(formData.durationDays) || 0,
  };
  const hasDurationEntered = isDurationEntered(
    formData.durationYears,
    formData.durationMonths,
    formData.durationDays
  );

  const handleDurationChange = (next: { years: string; months: string; days: string }) => {
    const updates: Partial<ContractWizardFormData> = {
      durationYears: next.years,
      durationMonths: next.months,
      durationDays: next.days,
    };
    if (formData.startDate) {
      const endDate = addDurationInclusive(formData.startDate, {
        years: Number(next.years) || 0,
        months: Number(next.months) || 0,
        days: Number(next.days) || 0,
      });
      if (endDate) {
        updates.endDate = endDate;
        updates.durationValue = String(contractMonthsFromDates(formData.startDate, endDate));
        updates.durationUnit = "month";
        // Only clear the "End date is required" error when an end_date was
        // actually derived — an all-zero/incomplete duration leaves endDate
        // unset, so a previously-shown validation error must stay visible
        // (PR #3069 review: clearing it unconditionally here was misleading).
        clearError("endDate");
      }
    }
    updateFormData(updates);
  };

  const handleStartDateChange = (date: string) => {
    const updates: Partial<ContractWizardFormData> = { startDate: date, firstPaymentDate: date };
    if (date && hasDurationEntered) {
      // A duration is already entered — recompute end_date for the new start.
      const endDate = addDurationInclusive(date, durationYmd);
      if (endDate) {
        updates.endDate = endDate;
        updates.durationValue = String(contractMonthsFromDates(date, endDate));
        updates.durationUnit = "month";
      }
    } else if (date && formData.endDate) {
      // No duration entered, but an end date already exists (manual entry) —
      // keep the Y/M/D display in sync with the new start/end span (AC3).
      const d = diffDurationInclusive(date, formData.endDate);
      updates.durationYears = String(d.years);
      updates.durationMonths = String(d.months);
      updates.durationDays = String(d.days);
      updates.durationValue = String(contractMonthsFromDates(date, formData.endDate));
      updates.durationUnit = "month";
    } else if (date) {
      // Neither a duration nor an end date exists yet — this is the FIRST
      // time a start date is picked on a fresh contract. Default to a 1-year
      // term (matches this wizard's long-standing default before Y/M/D
      // fields existed — the old durationValue/durationUnit default was
      // "12"/"month") so end_date auto-fills and step 1 stays unblocked
      // without forcing the user to touch the (still fully optional and
      // overridable) duration fields.
      const endDate = addDurationInclusive(date, { years: 1, months: 0, days: 0 });
      if (endDate) {
        updates.endDate = endDate;
        updates.durationYears = "1";
        updates.durationMonths = "0";
        updates.durationDays = "0";
        updates.durationValue = String(contractMonthsFromDates(date, endDate));
        updates.durationUnit = "month";
      }
    }
    updateFormData(updates);
    clearError("startDate");
    clearError("endDate");
  };

  const handleEndDateChange = (date: string) => {
    const updates: Partial<ContractWizardFormData> = { endDate: date };
    if (formData.startDate && date) {
      const d = diffDurationInclusive(formData.startDate, date);
      updates.durationYears = String(d.years);
      updates.durationMonths = String(d.months);
      updates.durationDays = String(d.days);
      updates.durationValue = String(contractMonthsFromDates(formData.startDate, date));
      updates.durationUnit = "month";
    }
    updateFormData(updates);
    clearError("endDate");
  };

  const getTenantName = (tenant: Tenant) => {
    if (tenant.tenant_type === "commercial") {
      return language === "ar"
        ? tenant.commercial_name || ""
        : tenant.commercial_name_en || tenant.commercial_name || "";
    }
    const arName = `${tenant.first_name || ""} ${tenant.last_name || ""}`.trim();
    const enName = `${tenant.first_name_en || ""} ${tenant.last_name_en || ""}`.trim();
    return language === "ar" ? arName : enName || arName;
  };

  const getPartyLabel = () => {
    return formData.contractType === "sale" 
      ? t("المشتري", "Buyer") 
      : t("المستأجر", "Tenant");
  };

  const getAmountLabel = () => {
    return formData.contractType === "sale"
      ? t("مبلغ البيع", "Sale Amount")
      : t("مبلغ الإيجار", "Rent Amount");
  };

  const clearError = (field: string) => {
    if (errors[field]) {
      const newErrors = { ...errors };
      delete newErrors[field];
      setErrors(newErrors);
    }
  };

  const handlePropertyChange = (value: string) => {
    // Property change resets the entire units list — different property means
    // a different unit pool, and the same-purpose lock from a previous pick
    // no longer applies.
    updateFormData({
      propertyId: value,
      unitId: "",
      units: [{ ...emptyContractUnitRow, serviceFees: { ...emptyContractUnitRow.serviceFees } }],
    });
    clearError("property");
    clearError("units");
  };

  // Multi-unit helpers. `units[0]` is the primary unit and is mirrored into
  // formData.unitId so any code that still reads the legacy single field
  // (validation, ReviewStep, useCreateContractWithInvoices) keeps working
  // even when the user hasn't added a second unit yet.
  const updateUnitRow = (index: number, updates: Partial<ContractUnitFormRow>) => {
    const newUnits = formData.units.map((row, i) =>
      i === index
        ? {
            ...row,
            ...updates,
            serviceFees: { ...row.serviceFees, ...(updates.serviceFees || {}) },
          }
        : row
    );
    updateFormData({
      units: newUnits,
      unitId: newUnits[0]?.unitId || "",
    });
    clearError("unit");
    clearError(`unit-${index}`);
  };

  const addUnitRow = () => {
    updateFormData({
      units: [
        ...formData.units,
        { ...emptyContractUnitRow, serviceFees: { ...emptyContractUnitRow.serviceFees } },
      ],
    });
  };

  const removeUnitRow = (index: number) => {
    if (formData.units.length <= 1) return;
    const newUnits = formData.units.filter((_, i) => i !== index);
    updateFormData({
      units: newUnits,
      unitId: newUnits[0]?.unitId || "",
    });
  };

  // Lock the unit picker to the first picked unit's purpose, so the user
  // can't mix residential + commercial on the same contract (CEO rule).
  // Look up against the full units list (not availableUnits) so the lock
  // also resolves for edit-mode contracts whose units are already 'rented'.
  const firstPickedUnit = units.find((u) => u.id === formData.units[0]?.unitId);
  const purposeLock = firstPickedUnit?.purpose ?? null;

  // Compute which unit IDs are already picked elsewhere on this contract, so
  // each picker only offers units that aren't already selected.
  const pickedUnitIds = new Set(formData.units.map((r) => r.unitId).filter(Boolean));

  const unitsForPicker = (currentRowUnitId: string) =>
    availableUnits.filter((u) => {
      if (u.id === currentRowUnitId) return true; // current selection always visible
      if (pickedUnitIds.has(u.id)) return false;
      if (purposeLock && u.purpose !== purposeLock) return false;
      return true;
    });

  const formatDisplayDate = (dateStr: string) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return format(date, "d/M/yyyy");
  };

  // Down-payment hint shown under the sale deposit input (spec item 2):
  // "Deducted from the total — remaining: X SAR". Computed once here (not an
  // inline IIFE in the JSX below).
  const downPaymentSaleTotal = Number(formData.totalAmount) || 0;
  const downPaymentAmount = computeDownPayment(
    downPaymentSaleTotal,
    Number(formData.depositAmount) || 0,
    formData.downPaymentType,
  );
  const downPaymentHint =
    downPaymentAmount > 0 ? (
      <p className="text-xs text-muted-foreground" data-testid="down-payment-hint">
        {t("يُخصم من إجمالي مبلغ البيع — المتبقي:", "Deducted from the total sale amount — remaining:")}{" "}
        <span className="font-medium text-foreground">
          {formatNumber(saleScheduledBalance(downPaymentSaleTotal, downPaymentAmount))}&nbsp;{RIYAL}
        </span>
      </p>
    ) : null;

  return (
    <div className="space-y-6">
      {employeeHasNoProperties && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>{t("لا توجد عقارات مخصّصة لك", "No properties assigned to you")}</AlertTitle>
          <AlertDescription>
            {t(
              "لا يمكنك إنشاء عقد قبل أن يخصّص لك المسؤول عقارًا واحدًا على الأقل. تواصل مع مسؤول الحساب لتخصيص العقارات.",
              "You can't create a contract until an administrator assigns at least one property to you. Please contact your account administrator."
            )}
          </AlertDescription>
        </Alert>
      )}

      {/* Row 1: Contract Type + (Sale Type, for sales) + Property */}
      <div className={cn("grid gap-4", isSale ? "grid-cols-3" : "grid-cols-2")}>
        {/* Contract Type */}
        <div className="space-y-2">
          <Label>{t("نوع العقد", "Contract Type")} <span className="text-destructive">*</span></Label>
          <Select
            value={formData.contractType}
            onValueChange={(val) =>
              updateFormData((prev) => ({
                ...prev,
                contractType: val as ContractWizardFormData["contractType"],
                // سعي on a sale contract is always paid by the BUYER (the
                // tenant-slot party) — flip the payer with the type so the
                // locked FeeRow shows the truth (the RPC coerces server-side
                // regardless). Tenant is also the lease default, so leaving
                // sale keeps a valid value.
                ...(val === "sale"
                  ? { brokerageFee: { ...prev.brokerageFee, payer: "tenant" as Payer } }
                  : {}),
              }))
            }
          >
            <SelectTrigger data-testid="contract-type-trigger">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {contractTypeOptions.map((type) => (
                <SelectItem key={type.value} value={type.value}>
                  {t(type.label, type.labelEn)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Sale Type — immediate vs off-plan (sale contracts only, spec item 1) */}
        {isSale && (
          <div className="space-y-2">
            <Label>{t("نوع البيع", "Sale Type")} <span className="text-destructive">*</span></Label>
            <Select
              value={formData.saleType}
              onValueChange={(val) => updateFormData({ saleType: val as SaleType })}
            >
              <SelectTrigger data-testid="sale-type-trigger">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SALE_TYPES.map((s) => (
                  <SelectItem key={s.value} value={s.value}>
                    {/* Direct literal t() calls (not the indirect s.label/s.labelEn
                        lookup) so the static i18n extractor picks these up. */}
                    {s.value === "offplan"
                      ? t("بيع على الخارطة", "Off-plan")
                      : t("بيع مباشر", "Immediate Sale")}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Property */}
        <div className="space-y-2">
          <Label>{t("العقار", "Property")} <span className="text-destructive">*</span></Label>
          <SearchableSelect
            triggerTestId="contract-property-trigger"
            items={properties}
            value={formData.propertyId}
            onValueChange={(v) => handlePropertyChange(v ?? "")}
            getKey={(p) => p.id}
            getLabel={(p) => (language === "ar" ? p.name : (p.name_en || p.name))}
            getSubtitle={(p) => p.district || null}
            getSearchString={(p) => `${p.name ?? ""} ${p.name_en ?? ""} ${p.district ?? ""}`}
            placeholder={
              propertiesLoading
                ? t("جار التحميل...", "Loading...")
                : t("اختر العقار", "Select property")
            }
            searchPlaceholder={t("ابحث باسم العقار، الحي", "Search by name, district")}
            emptyText={t("لا توجد عقارات مطابقة", "No matching property")}
            hasError={!!errors.property}
            disabled={propertiesLoading}
          />
          {errors.property && <p className="text-sm text-destructive">{errors.property}</p>}
        </div>
      </div>

      {/* Units & per-unit fees ────────────────────────────────────────────
          Multi-unit contracts: each row picks a unit (filtered to the same
          property + same purpose as the first picked unit) and captures
          its own rent_amount + the four EJAR annual service fees.

          Single-unit contracts (the common case) are just one row — the
          UI doesn't force the user to think about multi-unit unless they
          click "Add another unit". */}
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <Label>
            {t("الوحدات", "Units")} <span className="text-destructive">*</span>
            {formData.units.length > 1 && (
              <span className="ms-2 text-xs text-muted-foreground font-normal">
                ({formData.units.length} {t("وحدات", "units")})
              </span>
            )}
          </Label>
          {purposeLock && (
            <span className="text-xs text-muted-foreground">
              {t(
                `يمكن إضافة وحدات «${getPurposeLabel(purposeLock, "ar")}» فقط — وحدات الأغراض الأخرى مخفية`,
                `Only ${getPurposeLabel(purposeLock, "en")} units can be added — units of a different purpose are hidden`
              )}
            </span>
          )}
        </div>

        {formData.units.map((row, index) => {
          const pickerItems = unitsForPicker(row.unitId);
          const isFirst = index === 0;
          const rowError = errors[`unit-${index}`];
          return (
            <div
              key={index}
              className={cn(
                "rounded-lg border p-3 space-y-3",
                rowError ? "border-destructive" : "border-border"
              )}
            >
              <div className="flex items-end gap-2">
                <div className="flex-1 space-y-1">
                  <Label className="text-xs">
                    {isFirst
                      ? t("الوحدة الأساسية", "Primary unit")
                      : t(`الوحدة #${index + 1}`, `Unit #${index + 1}`)}
                  </Label>
                  <SearchableSelect
                    triggerTestId="contract-unit-trigger"
                    items={pickerItems}
                    value={row.unitId}
                    onValueChange={(v) => updateUnitRow(index, { unitId: v ?? "" })}
                    getKey={(u) => u.id}
                    getLabel={(u) => getUnitDisplayLabel(u, language === "ar" ? "ar" : "en")}
                    getSubtitle={(u) => (u.purpose ? t(`الغرض: ${u.purpose}`, `Purpose: ${u.purpose}`) : null)}
                    placeholder={
                      unitsLoading
                        ? t("جار التحميل...", "Loading...")
                        : t("اختر الوحدة", "Select unit")
                    }
                    searchPlaceholder={t("ابحث برقم الوحدة", "Search by unit number")}
                    emptyText={
                      purposeLock
                        ? t(
                            `لا توجد وحدات «${getPurposeLabel(purposeLock, "ar")}» أخرى متاحة`,
                            `No other ${getPurposeLabel(purposeLock, "en")} units available`
                          )
                        : t("لا توجد وحدات متاحة", "No available units")
                    }
                    hasError={!!rowError}
                    disabled={!formData.propertyId || unitsLoading}
                  />
                </div>

                <div className="w-40 space-y-1">
                  <Label className="text-xs">
                    {formData.contractType === "sale"
                      ? t("سعر البيع", "Sale Price")
                      : t("الإيجار السنوي", "Annual rent")}{" "}
                    <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    data-testid="contract-rent-input"
                    type="number"
                    min="0"
                    inputMode="decimal"
                    value={row.rentAmount}
                    onChange={(e) => updateUnitRow(index, { rentAmount: e.target.value })}
                    placeholder="0"
                    className={rowError ? "border-destructive" : undefined}
                  />
                </div>

                {!isFirst && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => removeUnitRow(index)}
                    aria-label={t("إزالة الوحدة", "Remove unit")}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                )}
              </div>

              {/* Services — every unit captures 8 annual amounts:
                    - 4 EJAR fees: sewage, maintenance, cleaning, security
                    - 4 utilities: electricity, water, gas, internet
                  Each row has: label, type (utilities only), annual amount,
                  payer. Tenant-paid amounts get folded into rent invoices
                  by the RPC (added as line items, no VAT). Owner-paid are
                  stored for record/EJAR only.
                  NOT shown for SALE contracts — a sale has no annual services
                  (spec item 3). Hidden here, in edit, and in the view. */}
              {!isSale && (
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">
                  {t("الخدمات السنوية", "Annual services")}
                </Label>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-xs text-muted-foreground">
                        <th className="text-start font-normal pb-1 pe-2">
                          {t("الخدمة", "Service")}
                        </th>
                        <th className="text-start font-normal pb-1 pe-2 w-32">
                          {t("النوع", "Type")}
                        </th>
                        <th className="text-start font-normal pb-1 w-28">
                          {t("المبلغ السنوي", "Annual")}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...SERVICE_FEE_KEYS, ...SERVICE_UTILITY_KEYS].map((key) => {
                        const entry = row.serviceFees[key] ?? emptyServiceEntry({
                          utility: (SERVICE_UTILITY_KEYS as readonly string[]).includes(key),
                        });
                        const isUtility = (SERVICE_UTILITY_KEYS as readonly string[]).includes(key);
                        return (
                          <tr key={key}>
                            <td className="py-1 pe-2 align-middle">
                              {t(SERVICE_LABELS[key].ar, SERVICE_LABELS[key].en)}
                            </td>
                            <td className="py-1 pe-2 align-middle">
                              {isUtility ? (
                                <Select
                                  value={entry.type ?? "not_available"}
                                  onValueChange={(v) =>
                                    updateUnitRow(index, {
                                      serviceFees: {
                                        ...row.serviceFees,
                                        [key]: { ...entry, type: v as UtilityType },
                                      },
                                    })
                                  }
                                >
                                  <SelectTrigger className="h-8 text-xs">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {UTILITY_TYPES_WITH_NA.map((u) => (
                                      <SelectItem key={u.value} value={u.value}>
                                        {t(u.label, u.labelEn)}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              ) : (
                                <span className="text-xs text-muted-foreground">—</span>
                              )}
                            </td>
                            <td className="py-1 align-middle">
                              {/* CEO decision 2026-05-17: services are always
                                  tenant-paid; owner never pays annual services.
                                  Payer dropdown removed — hardcoded to "tenant". */}
                              <Input
                                type="number"
                                min="0"
                                inputMode="decimal"
                                value={entry.amount}
                                onChange={(e) =>
                                  updateUnitRow(index, {
                                    serviceFees: {
                                      ...row.serviceFees,
                                      [key]: { ...entry, amount: e.target.value, payer: "tenant" },
                                    },
                                  })
                                }
                                placeholder="0"
                                className="h-8"
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-muted-foreground italic">
                  {t(
                    "المبالغ المدفوعة من المستأجر تضاف إلى فاتورة الإيجار (موزعة على الدفعات). الضريبة على الإيجار فقط.",
                    "Tenant-paid amounts are added to the rent invoice (split across payments). VAT applies to rent only."
                  )}
                </p>
              </div>
              )}

              {rowError && <p className="text-sm text-destructive">{rowError}</p>}
            </div>
          );
        })}

        {errors.unit && <p className="text-sm text-destructive">{errors.unit}</p>}

        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={addUnitRow}
          disabled={!formData.propertyId || unitsLoading}
          className="gap-1"
        >
          <Plus className="h-3 w-3" />
          {t("إضافة وحدة أخرى", "Add another unit")}
        </Button>
      </div>

      {/* Row 2: Tenant/Buyer with Add Button */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label>{getPartyLabel()} <span className="text-destructive">*</span></Label>
          <Button 
            type="button"
            variant="outline" 
            size="sm" 
            className="gap-1 h-8"
            onClick={() => setShowAddTenantDialog(true)}
          >
            <Plus className="h-3 w-3" />
            {formData.contractType === "sale"
              ? t("إضافة مشتري جديد", "Add New Buyer")
              : t("إضافة مستأجر جديد", "Add New Tenant")}
          </Button>
        </div>
        <SearchableSelect
          triggerTestId="contract-party-trigger"
          items={tenants}
          value={formData.tenantId}
          onValueChange={(v) => {
            updateFormData({ tenantId: v ?? "" });
            clearError("tenant");
          }}
          getKey={(tenant) => tenant.id}
          getLabel={(tenant) => getTenantName(tenant)}
          getSubtitle={(tenant) => tenant.id_number || tenant.phone || null}
          getSearchString={(tenant) =>
            `${getTenantName(tenant)} ${tenant.first_name ?? ""} ${tenant.last_name ?? ""} ${tenant.first_name_en ?? ""} ${tenant.last_name_en ?? ""} ${tenant.commercial_name ?? ""} ${tenant.commercial_name_en ?? ""} ${tenant.id_number ?? ""} ${tenant.phone ?? ""}`
          }
          placeholder={
            tenantsLoading
              ? t("جار التحميل...", "Loading...")
              : t("اختر المستأجر", "Select tenant")
          }
          searchPlaceholder={t("ابحث بالاسم، الهوية، الجوال", "Search by name, ID, phone")}
          emptyText={t("لا يوجد مستأجر مطابق", "No matching tenant")}
          hasError={!!errors.tenant}
          disabled={tenantsLoading}
        />
        {errors.tenant && <p className="text-sm text-destructive">{errors.tenant}</p>}
      </div>

      {/* Add Tenant Dialog */}
      <AddTenantDialog
        open={showAddTenantDialog}
        onOpenChange={setShowAddTenantDialog}
        onSuccess={(tenantId) => {
          updateFormData({ tenantId });
          clearError("tenant");
        }}
      />

      {/* Row 3: Start/Sale Date, End/Delivery Date, Duration (Ejar-style Y/M/D).
          Immediate sales have no term — only the sale date is shown; the end
          date + duration are hidden (spec item 4). Off-plan sales relabel the
          end date as the DELIVERY date. */}
      <div className={cn("grid gap-4", isImmediateSale ? "grid-cols-2" : "grid-cols-3")}>
        {/* Start Date */}
        <div className="space-y-2">
          <Label>
            {isSale ? t("تاريخ البيع", "Sale Date") : t("تاريخ بداية العقد", "Start Date")}{" "}
            <span className="text-destructive">*</span>
          </Label>
          <DatePicker
            id="contract-start-date"
            value={formData.startDate}
            onChange={handleStartDateChange}
            className={errors.startDate ? "border-destructive" : ""}
          />
          {errors.startDate && <p className="text-sm text-destructive">{errors.startDate}</p>}
        </div>

        {/* End / Delivery Date — hidden for immediate sales (no term). For
            off-plan it is the DELIVERY date and required; otherwise the lease
            end date. Editable directly or auto-filled from the duration fields. */}
        {!isImmediateSale && (
        <div className="space-y-2">
          <Label>
            {isOffplanSale
              ? t("تاريخ التسليم", "Delivery Date")
              : t("تاريخ نهاية العقد", "End Date")}{" "}
            <span className="text-destructive">*</span>
          </Label>
          <DatePicker
            id="contract-end-date"
            value={formData.endDate}
            onChange={handleEndDateChange}
            className={errors.endDate ? "border-destructive" : ""}
          />
          {errors.endDate && <p className="text-sm text-destructive">{errors.endDate}</p>}
        </div>
        )}

        {/* Contract Duration — Ejar-style years/months/days. Hidden for immediate
            sales. Optional elsewhere: fills the end/delivery date when a start
            date is set; also displays the computed breakdown (AC3). */}
        {!isImmediateSale && (
        <div className="space-y-2">
          <Label>{isOffplanSale ? t("مدة التسليم", "Delivery Term") : t("مدة العقد", "Contract Duration")}</Label>
          <DurationFields
            value={{
              years: formData.durationYears,
              months: formData.durationMonths,
              days: formData.durationDays,
            }}
            onChange={handleDurationChange}
          />
        </div>
        )}
      </div>

      {/* Row 4: Rent Amount, Deposit Amount.
          CEO decision 2026-05-17: Rent Amount is ALWAYS read-only for
          rent/sublease contracts — it is auto-summed from the per-unit
          annual rents entered above. The user sets rent per-unit; the RPC
          validates that p_rent_amount equals SUM(unit rents).
          Sale contracts keep the field editable (no per-unit rent concept). */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>
            {getAmountLabel()} <span className="text-destructive">*</span>
          </Label>
          {(() => {
            const isRentOrSublease =
              formData.contractType === "rent" ||
              formData.contractType === "sublease";
            const computedTotal = formData.units.reduce(
              (acc, u) => acc + (u.rentAmount ? Number(u.rentAmount) : 0),
              0
            );
            // AC4 — tenant-paid service fees that get folded into the contract
            // total + rent invoices. Owner-paid services are EJAR-record-only
            // and never invoiced, so they're excluded (matches the create RPC's
            // fold). Shown only as an informational line; the persisted header
            // total is recomputed server-side in the repository (Fortress R1).
            //
            // ROUNDING FIX: use deriveTenantPaidServicesTotal with the SAME
            // paymentsCount as the persistence path so the number shown here
            // matches the persisted total (per-payment rounding: ROUND(annual/n,2)×n).
            const paymentsCountForDisplay = Number(formData.paymentsCount) || 0;
            const unitsFoldShape = formData.units.map((u) => ({
              service_fees: Object.fromEntries(
                Object.entries(u.serviceFees).map(([key, entry]) => [
                  key,
                  { amount: entry.amount, payer: entry.payer },
                ])
              ),
            }));
            const annualTenantServicesTotal = deriveTenantPaidServicesTotal(
              unitsFoldShape,
              paymentsCountForDisplay,
            );
            // For rent/sublease: always show the computed sum and lock the field.
            // For sale: show formData.totalAmount and allow manual edit.
            const displayValue = isRentOrSublease
              ? computedTotal > 0
                ? String(computedTotal)
                : ""
              : formData.totalAmount;
            return (
              <>
                <div className="relative">
                  <Input
                    type="number"
                    value={displayValue}
                    readOnly={isRentOrSublease}
                    onChange={(e) => {
                      if (isRentOrSublease) return; // always locked for rent/sublease
                      updateFormData({ totalAmount: e.target.value });
                      clearError("totalAmount");
                    }}
                    placeholder="0"
                    className={cn(
                      "pe-12",
                      errors.totalAmount && "border-destructive",
                      isRentOrSublease && "bg-muted/40 cursor-not-allowed"
                    )}
                  />
                  <span className="absolute end-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
                    {RIYAL}
                  </span>
                </div>
                {isRentOrSublease && (
                  <p className="text-xs text-muted-foreground">
                    {t(
                      "يُحتسب تلقائيًا من مجموع الإيجارات لكل وحدة",
                      "Auto-computed from the sum of per-unit rents."
                    )}
                  </p>
                )}
                {/* AC4 — surface the tenant-paid annual service fees that get
                    folded into the contract total + rent invoices, so the user
                    sees they ARE counted (previously "saved but not counted").
                    The Rent Amount field stays rent-only (it drives proration);
                    this line shows the contract total including services. */}
                {isRentOrSublease && annualTenantServicesTotal > 0 && (
                  <div className="text-xs text-muted-foreground space-y-0.5 pt-1">
                    <p>
                      {t("الخدمات المدفوعة من المستأجر (سنويًا):", "Tenant-paid services (annual):")}{" "}
                      {annualTenantServicesTotal.toLocaleString()}&nbsp;{RIYAL}
                    </p>
                    <p className="font-medium text-foreground">
                      {t("إجمالي العقد شامل الخدمات:", "Contract total incl. services:")}{" "}
                      {(computedTotal + annualTenantServicesTotal).toLocaleString()}&nbsp;{RIYAL}
                    </p>
                  </div>
                )}
                {errors.totalAmount && (
                  <p className="text-sm text-destructive">{errors.totalAmount}</p>
                )}
              </>
            );
          })()}
        </div>

        <div className="space-y-2">
          {/* Deposit (rent/sublease) vs Down Payment (sale). A sale down payment
              can be a fixed SAR figure or a % of the sale amount, and is DEDUCTED
              from the total sale amount (spec item 2). */}
          <Label>{isSale ? t("الدفعة الأولى", "Down Payment") : t("مبلغ التأمين", "Deposit Amount")}</Label>
          {isSale ? (
            <>
              <div className="flex gap-2">
                <Select
                  value={formData.downPaymentType}
                  onValueChange={(val) => updateFormData({ downPaymentType: val as FeeType })}
                >
                  <SelectTrigger className="w-36" data-testid="down-payment-type-trigger">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FEE_TYPES.map((ft) => (
                      <SelectItem key={ft.value} value={ft.value}>
                        {t(ft.label, ft.labelEn)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="relative flex-1">
                  <Input
                    data-testid="down-payment-input"
                    type="number"
                    min="0"
                    inputMode="decimal"
                    value={formData.depositAmount}
                    onChange={(e) => updateFormData({ depositAmount: e.target.value })}
                    placeholder="0"
                    className="pe-12"
                  />
                  <span className="absolute end-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
                    {formData.downPaymentType === "percentage" ? "%" : RIYAL}
                  </span>
                </div>
              </div>
              {downPaymentHint}
            </>
          ) : (
            <div className="relative">
              <Input
                type="number"
                value={formData.depositAmount}
                onChange={(e) => updateFormData({ depositAmount: e.target.value })}
                placeholder="0"
                className="pe-12"
              />
              <span className="absolute end-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
                {RIYAL}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Preferred payment channel — for an IMMEDIATE sale there is no payment
          step, so the channel lives here under sale amount + down payment
          (spec item 1). Off-plan/rent collect it on the Payment step instead. */}
      {isImmediateSale && (
        <div className="space-y-2">
          <Label>{t("طريقة الدفع المفضلة", "Preferred Payment Method")}</Label>
          <Select
            value={formData.preferredPaymentMethod}
            onValueChange={(val) => updateFormData({ preferredPaymentMethod: val as PaymentMethod })}
          >
            <SelectTrigger data-testid="immediate-sale-payment-method">
              <SelectValue placeholder={t("اختر طريقة الدفع", "Select payment method")} />
            </SelectTrigger>
            <SelectContent>
              {PAYMENT_METHODS.map((method) => (
                <SelectItem key={method.value} value={method.value}>
                  {t(method.label, method.labelEn)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Fees Section */}
      <div className="space-y-4 border-t pt-4">
        <Label className="text-base font-semibold">{t("الرسوم", "Fees")}</Label>

        {/* Brokerage Fee */}
        <FeeRow
          testId="fee-row-brokerage"
          label={t("رسوم السعي", "Brokerage Fee")}
          fee={formData.brokerageFee}
          totalAmount={formData.totalAmount}
          onChange={(updater) =>
            updateFormData((prev) => ({ ...prev, brokerageFee: updater(prev.brokerageFee) }))
          }
          t={t}
          payers={payerOptions(formData.contractType)}
          payerLocked={formData.contractType === "sale"}
        />

        {/* Marketing Fee */}
        <FeeRow
          testId="fee-row-marketing"
          label={t("رسوم التسويق", "Marketing Fee")}
          fee={formData.marketingFee}
          totalAmount={formData.totalAmount}
          onChange={(updater) =>
            updateFormData((prev) => ({ ...prev, marketingFee: updater(prev.marketingFee) }))
          }
          t={t}
          payers={payerOptions(formData.contractType)}
        />

        {/* Contract Management Fee */}
        <FeeRow
          testId="fee-row-contract-management"
          label={t("رسوم إدارة العقود", "Contract Management Fee")}
          fee={formData.contractManagementFee}
          totalAmount={formData.totalAmount}
          onChange={(updater) =>
            updateFormData((prev) => ({ ...prev, contractManagementFee: updater(prev.contractManagementFee) }))
          }
          t={t}
          payers={payerOptions(formData.contractType)}
        />

        {/* Property Management Fee — NOT applicable to sale contracts (spec item 3). */}
        {!isSale && (
          <FeeRow
            testId="fee-row-property-management"
            label={t("رسوم إدارة الأملاك", "Property Management Fee")}
            fee={formData.propertyManagementFee}
            totalAmount={formData.totalAmount}
            onChange={(updater) =>
              updateFormData((prev) => ({ ...prev, propertyManagementFee: updater(prev.propertyManagementFee) }))
            }
            t={t}
            payers={payerOptions(formData.contractType)}
          />
        )}
      </div>
    </div>
  );
}

